import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Upload,
  FileText,
  Users,
  Edit,
  Trash2,
  Eye,
  Loader,
  AlertCircle,
  XCircle,
  CheckCircle,
  X,
  Download,
  Clock,
} from "lucide-react";
import {
  getSegments,
  createSegment,
  deleteSegment,
  uploadContacts,
  getContacts,
} from "../apis/segments";
import { toast } from "react-toastify";

const ContactLists = () => {
  const [segments, setSegments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    segment_name: "",
    description: "",
  });
  const [csvFile, setCsvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    setIsLoading(true);
    setError("");
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getSegments(authToken);
      setSegments(response.data.segments || []);
    } catch (err) {
      console.error("Error fetching segments:", err);
      setError(err.response?.data?.error || "Failed to load contact lists");
      toast.error(err.response?.data?.error || "Failed to load contact lists");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      { contact_name: "John Doe", email: "john.doe@example.com", phone: "+1234567890" },
      { contact_name: "Jane Smith", email: "jane.smith@example.com", phone: "+1234567891" },
      { contact_name: "Bob Johnson", email: "bob.johnson@example.com", phone: "+1234567892" },
    ];

    // Convert to CSV
    const headers = ["contact_name", "email", "phone"];
    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_contacts.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateSegment = async (e) => {
    e.preventDefault();
    if (!formData.segment_name.trim()) {
      toast.error("Segment name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // First create the segment
      const createResponse = await createSegment(authToken, formData);
      const newSegmentId = createResponse.data.segment.segment_id;
      
      // If CSV file is provided, upload it
      if (csvFile) {
        try {
          const uploadResponse = await uploadContacts(authToken, newSegmentId, csvFile);
          
          // Show summary message
          const total = uploadResponse.data.total || uploadResponse.data.inserted || 0;
          const invalid = uploadResponse.data.invalid || 0;
          const duplicates = uploadResponse.data.duplicates || 0;
          
          let message = `Contact list created. ${total} number(s) uploaded`;
          if (invalid > 0) {
            message += `, ${invalid} invalid number(s)`;
          }
          if (duplicates > 0) {
            message += `. ${duplicates} duplicate number(s) skipped.`;
          } else {
            message += `.`;
          }
          
          if (invalid > 0 || duplicates > 0) {
            toast.warning(message);
          } else {
            toast.success(message);
          }
        } catch (uploadErr) {
          console.error("Error uploading contacts:", uploadErr);
          toast.warning("Contact list created but failed to upload contacts. You can upload them later.");
        }
      } else {
        toast.success("Contact list created successfully");
      }
      
      setShowCreateModal(false);
      setFormData({ segment_name: "", description: "" });
      setCsvFile(null);
      await fetchSegments();
    } catch (err) {
      console.error("Error creating segment:", err);
      toast.error(err.response?.data?.error || "Failed to create contact list");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm("Are you sure you want to delete this contact list? All contacts will also be deleted.")) {
      return;
    }

    try {
      const authToken = localStorage.getItem("userAccessToken");
      await deleteSegment(authToken, segmentId);
      toast.success("Contact list deleted successfully");
      await fetchSegments();
    } catch (err) {
      console.error("Error deleting segment:", err);
      toast.error(err.response?.data?.error || "Failed to delete contact list");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setCsvFile(file);
      } else {
        toast.error("Please select a CSV file");
        e.target.value = "";
      }
    }
  };

  const handleUploadContacts = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsSubmitting(true);
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await uploadContacts(
        authToken,
        selectedSegment.segment_id,
        csvFile
      );
      
      // Show summary message with validation and duplicate info
      const total = response.data.total || response.data.inserted || 0;
      const invalid = response.data.invalid || 0;
      const duplicates = response.data.duplicates || 0;
      
      let message = `${total} number(s) uploaded`;
      if (invalid > 0) {
        message += `, ${invalid} invalid number(s)`;
      }
      if (duplicates > 0) {
        message += `. ${duplicates} duplicate number(s) skipped.`;
      } else {
        message += `.`;
      }
      
      if (invalid > 0 || duplicates > 0) {
        toast.warning(message);
      } else {
        toast.success(message);
      }
      
      setShowUploadModal(false);
      setCsvFile(null);
      await fetchSegments();
    } catch (err) {
      console.error("Error uploading contacts:", err);
      toast.error(err.response?.data?.error || "Failed to upload contacts");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewContacts = async (segment) => {
    setSelectedSegment(segment);
    setShowContactsModal(true);
    setIsLoadingContacts(true);
    setContacts([]);

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getContacts(authToken, segment.segment_id);
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      toast.error(err.response?.data?.error || "Failed to load contacts");
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const handleDownloadContacts = async (segment) => {
    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // Fetch all contacts (use a large limit to get all)
      const response = await getContacts(authToken, segment.segment_id, {
        limit: 10000,
        offset: 0,
      });
      
      const contactsToDownload = response.data.contacts || [];
      
      if (contactsToDownload.length === 0) {
        toast.info("No contacts to download");
        return;
      }

      // Convert to CSV
      const headers = ["contact_name", "email", "phone", "status"];
      const csvContent = [
        headers.join(","),
        ...contactsToDownload.map((contact) =>
          headers
            .map((header) => {
              const value = contact[header] || "";
              // Escape quotes and wrap in quotes if contains comma or newline
              return `"${value.toString().replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${segment.segment_name.replace(/[^a-z0-9]/gi, "_")}_contacts.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${contactsToDownload.length} contacts`);
    } catch (err) {
      console.error("Error downloading contacts:", err);
      toast.error(err.response?.data?.error || "Failed to download contacts");
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: {
        icon: CheckCircle,
        label: "Active",
        class: "bg-emerald-50 text-emerald-700 border-emerald-200",
      },
      inactive: {
        icon: XCircle,
        label: "Inactive",
        class: "bg-red-50 text-red-700 border-red-200",
      },
    };
    return configs[status] || configs.active;
  };

  const filteredSegments = segments.filter((segment) => {
    const matchesSearch =
      segment.segment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || segment.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#f3f3f3] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contact Lists</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your contact segments</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-900 text-sm font-semibold rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-300"
              style={{ backgroundColor: "#eefa94" }}
            >
              <Plus size={18} />
              New Contact List
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Total Lists
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {segments.length}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <FileText className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">Active Lists</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    {segments.filter((s) => s.status === "active").length}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Total Contacts
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {segments.reduce(
                      (sum, s) => sum + (Number(s.contact_count) || 0),
                      0
                    )}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                  <Users className="text-purple-600" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-semibold">{error}</p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800 p-1 rounded-lg hover:bg-red-100 transition-colors"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search contact lists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 text-sm font-semibold border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white min-w-[150px]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader
                size={40}
                className="animate-spin text-gray-900 mx-auto mb-4"
              />
              <p className="text-sm font-semibold text-gray-600">Loading contact lists...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && segments.length === 0 && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
                <FileText className="text-gray-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                No contact lists yet
              </h3>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                Create your first contact list to start organizing contacts
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-5 py-3 text-gray-900 text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Plus size={18} />
                Create Your First Contact List
              </button>
            </div>
          </div>
        )}

        {/* Segments Grid */}
        {!isLoading && filteredSegments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSegments.map((segment) => {
              const statusConfig = getStatusBadge(segment.status);
              const StatusIcon = statusConfig.icon;

              return (
                <motion.div
                  key={segment.segment_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 mb-1 truncate">
                        {segment.segment_name}
                      </h3>
                      {segment.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 font-medium">
                          {segment.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border flex-shrink-0 ml-2 ${statusConfig.class}`}
                    >
                      <StatusIcon size={12} />
                      {statusConfig.label}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Users size={16} />
                      <span className="font-bold">
                        {Number(segment.contact_count) || 0}
                      </span>
                      <span className="font-medium text-gray-600">contacts</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                      <Clock size={12} />
                      {new Date(segment.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewContacts(segment)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors border border-gray-200"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSegment(segment);
                        setShowUploadModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors border border-blue-200"
                    >
                      <Upload size={14} />
                      Upload
                    </button>
                    <button
                      onClick={() => handleDownloadContacts(segment)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors border border-emerald-200"
                      title="Download contact list as CSV"
                    >
                      <Download size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteSegment(segment.segment_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-red-200"
                      title="Delete contact list"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* No Results */}
        {!isLoading && segments.length > 0 && filteredSegments.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Search className="text-gray-400 mx-auto mb-3" size={48} />
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No matching contact lists
            </h3>
            <p className="text-sm text-gray-600 font-medium">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Create Contact List
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ segment_name: "", description: "" });
                  setCsvFile(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSegment} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  List Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.segment_name}
                  onChange={(e) =>
                    setFormData({ ...formData, segment_name: e.target.value })
                  }
                  className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                  placeholder="Enter contact list name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                  placeholder="Enter description (optional)"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-bold text-gray-700">
                    Upload Contacts (CSV)
                  </label>
                  <button
                    type="button"
                    onClick={downloadSampleCSV}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-bold"
                  >
                    <Download size={14} />
                    Sample CSV
                  </button>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csvFileInputCreate"
                  />
                  <label
                    htmlFor="csvFileInputCreate"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="text-gray-400 mb-3" size={32} />
                    <span className="text-sm font-semibold text-gray-700 mb-1">
                      {csvFile ? csvFile.name : "Click to select CSV file"}
                    </span>
                    <span className="text-xs text-gray-500 font-medium">
                      (optional)
                    </span>
                    {csvFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCsvFile(null);
                          document.getElementById("csvFileInputCreate").value = "";
                        }}
                        className="mt-3 text-xs text-red-600 hover:text-red-700 font-bold"
                      >
                        Remove file
                      </button>
                    )}
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium">
                  CSV should have columns: contact_name, email, phone (at least phone or email is required)
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ segment_name: "", description: "" });
                    setCsvFile(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-900 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    "Create List"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Upload Modal - For existing segments */}
      {showUploadModal && selectedSegment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Upload Contacts
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setCsvFile(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3 font-medium">
                Upload CSV file for: <strong className="text-gray-900">{selectedSegment.segment_name}</strong>
              </p>
              <div className="flex items-center justify-end mb-3">
                <button
                  type="button"
                  onClick={downloadSampleCSV}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-bold"
                >
                  <Download size={14} />
                  Sample CSV
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4 font-medium bg-gray-50 border border-gray-200 rounded-xl p-3">
                CSV should have columns: contact_name, email, phone (at least phone or email is required)
              </p>
            </div>

            <form onSubmit={handleUploadContacts} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  CSV File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csvFileInput"
                  />
                  <label
                    htmlFor="csvFileInput"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="text-gray-400 mb-3" size={32} />
                    <span className="text-sm font-semibold text-gray-700">
                      {csvFile ? csvFile.name : "Click to select CSV file"}
                    </span>
                    {csvFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCsvFile(null);
                          document.getElementById("csvFileInput").value = "";
                        }}
                        className="mt-3 text-xs text-red-600 hover:text-red-700 font-bold"
                      >
                        Remove file
                      </button>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvFile(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !csvFile}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-900 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    "Upload"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Contacts View Modal */}
      {showContactsModal && selectedSegment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedSegment.segment_name}
                </h2>
                <p className="text-sm text-gray-600 mt-1 font-medium">
                  {contacts.length} contacts
                </p>
              </div>
              <div className="flex items-center gap-2">
                {contacts.length > 0 && (
                  <button
                    onClick={() => handleDownloadContacts(selectedSegment)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors border border-emerald-200"
                    title="Download contacts as CSV"
                  >
                    <Download size={16} />
                    Download CSV
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowContactsModal(false);
                    setSelectedSegment(null);
                    setContacts([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingContacts ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <Loader
                      size={40}
                      className="animate-spin text-gray-900 mx-auto mb-4"
                    />
                    <p className="text-sm font-semibold text-gray-600">Loading contacts...</p>
                  </div>
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="text-gray-400 mx-auto mb-4" size={56} />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No contacts found</h3>
                  <p className="text-sm text-gray-600 font-medium">Upload a CSV file to add contacts to this list</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                          Phone
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                          WhatsApp Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {contacts.map((contact) => {
                        const statusConfig = getStatusBadge(contact.status);
                        const StatusIcon = statusConfig.icon;

                        const getValidationBadge = (status, phone) => {
                          if (status === "inactive") {
                            return {
                              icon: XCircle,
                              label: "Invalid",
                              class: "bg-red-50 text-red-700 border-red-200",
                            };
                          } else if (status === "active") {
                            return {
                              icon: CheckCircle,
                              label: phone ? "Valid" : "Pending",
                              class: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            };
                          } else {
                            return {
                              icon: Clock,
                              label: "Pending",
                              class: "bg-yellow-50 text-yellow-700 border-yellow-200",
                            };
                          }
                        };
                        const validationConfig = getValidationBadge(contact.status, contact.phone);
                        const ValidationIcon = validationConfig.icon;

                        return (
                          <tr
                            key={contact.contact_id}
                            className="hover:bg-gray-50 transition"
                          >
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                              {contact.contact_name || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-600">
                              {contact.email || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-600">
                              {contact.phone || "N/A"}
                            </td>
                            <td className="px-4 py-3">
                              {contact.phone ? (
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${validationConfig.class}`}
                                  title={
                                    contact.status === "inactive"
                                      ? "Invalid phone number or not registered on WhatsApp"
                                      : contact.status === "active"
                                      ? "Valid WhatsApp number"
                                      : "Validation pending"
                                  }
                                >
                                  <ValidationIcon size={12} />
                                  {validationConfig.label}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 font-medium">N/A</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${statusConfig.class}`}
                              >
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContactLists;
